import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  UserIcon, 
  ArrowRightIcon, 
} from '@heroicons/react/24/outline';
import { EyeIcon, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { Select } from 'antd';

const { Option } = Select;

const AVAILABLE_DESIGNATIONS = [
  { value: "CFP", label: "CFP®️" },
  { value: "CFA", label: "CFA" },
  { value: "ChFC", label: "ChFC" },
  { value: "CIMA", label: "CIMA®️" },
  { value: "CPWA", label: "CPWA®️" },
  { value: "CEPA", label: "CEPA®️" },
  { value: "CLU", label: "CLU®️" },
  { value: "RICP", label: "RICP®️" },
  { value: "AIF", label: "AIF®️" },
  { value: "CRPC", label: "CRPC®️" },
  { value: "QKA", label: "QKA®️" },
  { value: "QPA", label: "QPA®️" },
  { value: "TGPC", label: "TGPC®️" },
  { value: "CPC", label: "CPC®️" },
  { value: "CPFA", label: "CPFA®️" },
  { value: "NQPA", label: "NQPA®️" },
  { value: "PPC", label: "PPC™" },
  { value: "AIFA", label: "AIFA®️" },
  { value: "ERPA", label: "ERPA" },
  { value: "CRPS", label: "CRPS®️" },
  { value: "FCA", label: "FCA" },
  { value: "ARA", label: "ARA Member" },
  { value: "MBA", label: "MBA" },
  { value: "JD", label: "JD" },
  { value: "MD", label: "MD" },
  { value: "PhD", label: "PhD" },
];

const selectStyles = {
  width: '100%',
  height: '42px',
  borderRadius: '0.5rem',
};

const AdminSignupForm = () => {
  // Form data state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    companyDisclaimer: '',
    companyAddress: '',
    advisor1Name: '',
    advisor1Designation: '',
    advisor2Name: '',
    advisor2Designation: '',
    advisor3Name: '',
    advisor3Designation: '',
    adminKey: '' // New field for admin key
  });

  // Other state variables
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [adminKeyTouched, setAdminKeyTouched] = useState(false);

  // TOTP state
  const [totpUri, setTotpUri] = useState('');
  const [showTotpModal, setShowTotpModal] = useState(false);

  const navigate = useNavigate();

  // Function to check password strength
  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  // Function to determine if password is strong
  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(criteria => criteria);
  };

  // Handle input changes for most fields
  const handleChange = (e) => {
    const { name, value } = e.target;

    // If password field, check strength
    if (name === 'password') {
      checkPasswordStrength(value);
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle input changes specifically for companyName to trim spaces
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for textareas and advisor names
    if (name === 'companyAddress' || 
        name === 'companyDisclaimer' || 
        name.includes('advisor') || 
        name.includes('Name')) {
      setFormData(prev => ({
        ...prev,
        [name]: value  // Don't trim spaces for these fields
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value.trim()  // Trim spaces for other fields
      }));
    }
  };

  // Generate password validation message
  const getPasswordValidationMessage = (password) => {
    if (!password) return '';
    const missing = [];
    if (password.length < 8) missing.push('8 characters');
    if (!/[A-Z]/.test(password)) missing.push('uppercase letter');
    if (!/[a-z]/.test(password)) missing.push('lowercase letter');
    if (!/\d/.test(password)) missing.push('number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) missing.push('special character');

    if (missing.length === 0) return '';
    if (missing.length === 1) {
      return `Password must contain at least one ${missing[0]}`;
    }
    const lastItem = missing.pop();
    return `Password must contain at least one ${missing.join(', ')} and one ${lastItem}`;
  };

  // Generate password match message
  const getPasswordMatchMessage = () => {
    if (confirmPasswordTouched && formData.password !== formData.confirmPassword) {
      return 'Passwords must match';
    }
    return '';
  };

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Generate email validation message
  const getEmailValidationMessage = (email) => {
    if (!email) return '';
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Define required fields
    const requiredFields = [
      'email',
      'password',
      'firstName',
      'lastName',
      'companyName',
      'companyPhone',
      'companyEmail',
      'companyWebsite',
      'companyDisclaimer',
      'companyAddress',
      'advisor1Name',
      'advisor1Designation',
      'adminKey' // Added admin key as required
    ];

    // Validate only required fields
    const emptyRequiredFields = requiredFields.filter(field => !formData[field]);
    if (emptyRequiredFields.length > 0) {
      setError(`Please fill in all required fields: ${emptyRequiredFields.map(key => key).join(', ')}`);
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate email format
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check password strength
    if (!isPasswordStrong()) {
      setError('Please ensure your password meets all strength requirements');
      return;
    }

    // Check terms acceptance
    if (!acceptedTerms) {
      setError('Please accept the Terms and Privacy Policy to continue');
      return;
    }

    setIsLoading(true);

    try {
      const submitData = {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        company_name: formData.companyName,
        company_phone: formData.companyPhone,
        company_email: formData.companyEmail,
        company_website: formData.companyWebsite,
        company_disclaimer: formData.companyDisclaimer,
        company_address: formData.companyAddress,
        advisor1_name: formData.advisor1Name,
        advisor1_designation: formData.advisor1Designation,
        advisor2_name: formData.advisor2Name?.trim() || '',
        advisor2_designation: formData.advisor2Designation?.trim() || '',
        advisor3_name: formData.advisor3Name?.trim() || '',
        advisor3_designation: formData.advisor3Designation?.trim() || '',
        admin_key: formData.adminKey // Added admin key
      };
      
      const response = await fetch('https://plansync-backend-234288438538.us-central1.run.app/api/auth/signup-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Store the totp_uri and show the modal
      setTotpUri(data.totp_uri);
      setShowTotpModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this component for section headers
  const SectionHeader = ({ title, required }) => (
    <div className="flex items-center gap-2 mb-4">
      <h3 className="text-xl font-semibold text-gray-800 leading-none">{title}</h3>
      {required && (
        <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full leading-none">
          Required
        </span>
      )}
    </div>
  );

  return (
    <>
      {/* Signup Form */}
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-bl from-sky-400 to-blue-800 py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white bg-opacity-100 backdrop-filter backdrop-blur-lg rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white border-opacity-20 my-8"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Create Admin Account</h1>
            <p className="text-gray-500">Join as an administrator</p>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-red-500 bg-opacity-20 border-l-4 border-red-500 text-gray-800 p-4 mb-6 rounded-md"
              role="alert"
            >
              <p>{error}</p>
            </motion.div>
          )}

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Admin Key Section */}
            <div className="space-y-4">
              <SectionHeader title="Admin Access Key" required />
              
              <div>
                <input
                  type="text"
                  name="adminKey"
                  value={formData.adminKey}
                  onChange={handleInputChange}
                  onBlur={() => setAdminKeyTouched(true)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter the admin key provided to you"
                  required
                />
                {adminKeyTouched && !formData.adminKey && (
                  <p className="mt-1 text-sm text-red-600">
                    Admin key is required
                  </p>
                )}
              </div>
            </div>

            {/* Account Information Section */}
            <div className="space-y-4">
              <SectionHeader title="Account Information" required />
              
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => setEmailTouched(true)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Email address"
                  required
                />
                {emailTouched && !emailRegex.test(formData.email) && (
                  <p className="mt-1 text-sm text-red-600">
                    {getEmailValidationMessage(formData.email)}
                  </p>
                )}
              </div>

              <div className="flex space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    className="w-full px-4 py-2 bg-white border border-gray-300 focus:border-blue-500 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-lg transition-all duration-300"
                    required
                  />
                </div>

                <div className="flex-1">
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    className="w-full px-4 py-2 bg-white border border-gray-300 focus:border-blue-500 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-lg transition-all duration-300"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Company Information Section */}
            <div className="space-y-4">
              <SectionHeader title="Company Information" required />
              
              <div className="grid gap-4">
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Company/Firm Name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />

                <input
                  type="tel"
                  name="companyPhone"
                  value={formData.companyPhone}
                  onChange={handleInputChange}
                  placeholder="Company Phone"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <input
                    type="email"
                    name="companyEmail"
                    value={formData.companyEmail}
                    onChange={handleInputChange}
                    placeholder="Company Email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                />

                <input
                  type="url"
                  name="companyWebsite"
                  value={formData.companyWebsite}
                  onChange={handleInputChange}
                  placeholder="Company Website"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />

                <textarea
                  name="companyAddress"
                  value={formData.companyAddress}
                  onChange={handleInputChange}
                  placeholder="Company Address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  required
                  rows="3"
                  spellCheck="false"
                />

                <textarea
                  name="companyDisclaimer"
                  value={formData.companyDisclaimer}
                  onChange={handleInputChange}
                  placeholder="Company Disclaimer"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  required
                  rows="3"
                  spellCheck="false"
                />
              </div>
            </div>

            {/* Advisors Section */}
            <div className="space-y-6">
              <SectionHeader title="Advisors Information" />
              
              {/* Primary Advisor */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h4 className="text-md font-medium text-gray-700 leading-none">Primary Advisor</h4>
                  <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full leading-none">
                    Required
                  </span>
                </div>
                <div className="grid gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      name="advisor1Name"
                      value={formData.advisor1Name}
                      onChange={handleInputChange}
                      placeholder="Advisor Name"
                      className="w-full h-[42px] pl-4 pr-4 bg-white border border-gray-300 focus:border-blue-500 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-lg transition-all duration-300"
                      required
                    />
                  </div>
                  <Select
                    mode="multiple"
                    style={selectStyles}
                    placeholder="Select Designations"
                    value={formData.advisor1Designation ? formData.advisor1Designation.split(',') : []}
                    onChange={(value) => handleInputChange({
                      target: {
                        name: 'advisor1Designation',
                        value: value.join(',')
                      }
                    })}
                    className="rounded-lg"
                    maxTagCount={3}
                    maxTagTextLength={6}
                    dropdownStyle={{ borderRadius: '0.5rem' }}
                    ClassName="rounded-lg shadow-lg"
                  >
                    {AVAILABLE_DESIGNATIONS.map(designation => (
                      <Option key={designation.value} value={designation.value}>
                        {designation.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Additional Advisors */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h4 className="text-md font-medium text-gray-700 leading-none">Additional Advisors</h4>
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full leading-none">
                    Optional
                  </span>
                </div>
                
                {/* Advisor 2 */}
                <div className="grid gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      name="advisor2Name"
                      value={formData.advisor2Name}
                      onChange={handleInputChange}
                      placeholder="Second Advisor Name"
                      className="w-full h-[42px] pl-4 pr-4 bg-white border border-gray-300 focus:border-blue-500 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-lg transition-all duration-300"
                    />
                  </div>
                  <Select
                    mode="multiple"
                    style={selectStyles}
                    placeholder="Select Designations"
                    value={formData.advisor2Designation ? formData.advisor2Designation.split(',') : []}
                    onChange={(value) => handleInputChange({
                      target: {
                        name: 'advisor2Designation',
                        value: value.join(',')
                      }
                    })}
                    className="rounded-lg"
                    maxTagCount={3}
                    maxTagTextLength={6}
                    dropdownStyle={{ borderRadius: '0.5rem' }}
                    dropdownClassName="rounded-lg shadow-lg"
                  >
                    {AVAILABLE_DESIGNATIONS.map(designation => (
                      <Option key={designation.value} value={designation.value}>
                        {designation.label}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Advisor 3 */}
                <div className="grid gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      name="advisor3Name"
                      value={formData.advisor3Name}
                      onChange={handleInputChange}
                      placeholder="Third Advisor Name"
                      className="w-full h-[42px] pl-4 pr-4 bg-white border border-gray-300 focus:border-blue-500 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-lg transition-all duration-300"
                    />
                  </div>
                  <Select
                    mode="multiple"
                    style={selectStyles}
                    placeholder="Select Designations"
                    value={formData.advisor3Designation ? formData.advisor3Designation.split(',') : []}
                    onChange={(value) => handleInputChange({
                      target: {
                        name: 'advisor3Designation',
                        value: value.join(',')
                      }
                    })}
                    className="rounded-lg"
                    maxTagCount={3}
                    maxTagTextLength={6}
                    dropdownStyle={{ borderRadius: '0.5rem' }}
                    dropdownClassName="rounded-lg shadow-lg"
                  >
                    {AVAILABLE_DESIGNATIONS.map(designation => (
                      <Option key={designation.value} value={designation.value}>
                        {designation.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Password Section */}
            <div className="space-y-4">
              <SectionHeader title="Password" required />
              
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    onBlur={() => setPasswordTouched(true)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>

                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm Password"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    onBlur={() => setConfirmPasswordTouched(true)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Terms and Submit Section */}
            <div className="space-y-6">
              <div className="flex items-start space-x-2 text-sm">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  required
                />
                <label htmlFor="terms" className="text-gray-600">
                  I have read and agree to the{' '}
                  <a href="https://drive.google.com/file/d/1AACdaA-dwVBOkH6GQ4msDcML35fe7PSB/view?usp=sharing" className="text-blue-600 hover:text-blue-700 underline" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="https://drive.google.com/file/d/1T1YtQA8wW7MCw-RT-7wdJhWtjPqr885R/view?usp=sharing" className="text-blue-600 hover:text-blue-700 underline" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className={`w-full bg-gradient-to-bl from-sky-400 to-blue-800 text-white py-3 rounded-full flex items-center justify-center space-x-2 ${
                  isLoading || !acceptedTerms ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
                disabled={isLoading || !acceptedTerms}
              >
                <span className="font-bold">{isLoading ? 'Creating Admin Account...' : 'Sign Up as Admin'}</span>
                {!isLoading && <ArrowRightIcon className="h-5 w-5" />}
              </motion.button>
            </div>
          </form>

          {/* Link to Login */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-8 text-center"
          >
            <p className="text-gray-600">Already have an account?</p>
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-300">
              Log In
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* TOTP Setup Modal */}
      {showTotpModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md mx-auto">
            <h2 className="text-2xl font-bold mb-4">Set Up Two-Factor Authentication</h2>
            <p className="mb-4">
              Scan the QR code below with your authenticator app (e.g., Google Authenticator, Authy).
            </p>
            <div className="flex justify-center mb-4">
              <QRCodeCanvas value={totpUri} size={256} />
            </div>
            <p className="mb-4">
              After scanning, enter the code generated by your authenticator app when prompted during login.
            </p>
            <button
              onClick={() => {
                setShowTotpModal(false);
                navigate('/login');
              }}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-full hover:bg-blue-700 transition-colors duration-300"
            >
              Proceed to Login
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSignupForm; 